import React, { useEffect, useRef, useState } from "react";

/**
 * Smoothly counts up to `value` once the element scrolls into view.
 * Uses a plain IntersectionObserver + requestAnimationFrame — deliberately
 * avoids framer-motion's useInView/animate because they had a race condition
 * on mobile that left ~half of the counters stuck at 0.
 */
export const CountUp = ({
  value,
  suffix = "",
  prefix = "",
  duration = 1.6,
  format,
  className = "",
  testId,
}) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(`${prefix}0${suffix}`);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Respect reduced motion — snap straight to final value.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const finalText = `${prefix}${
      format ? format(value) : Math.round(value).toLocaleString("en-GB")
    }${suffix}`;

    if (prefersReduced) {
      setDisplay(finalText);
      return;
    }

    let rafId = 0;
    let startedAt = 0;
    let hasRun = false;

    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    const tick = (now) => {
      if (!startedAt) startedAt = now;
      const elapsed = (now - startedAt) / 1000;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutExpo(t);
      const current = value * eased;
      const formatted = format
        ? format(current)
        : Math.round(current).toLocaleString("en-GB");
      setDisplay(`${prefix}${formatted}${suffix}`);
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setDisplay(finalText);
      }
    };

    const start = () => {
      if (hasRun) return;
      hasRun = true;
      rafId = requestAnimationFrame(tick);
    };

    // Fallback: if IntersectionObserver isn't supported, run immediately.
    if (typeof IntersectionObserver === "undefined") {
      start();
      return () => cancelAnimationFrame(rafId);
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            start();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(node);

    return () => {
      io.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [value, prefix, suffix, duration, format]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {display}
    </span>
  );
};
